import SearchIcon from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import {
  ACCESSIBILITY_LABEL_HU,
  DOCUMENT_TYPE_LABEL_HU,
  FOLDER_STATUS_LABEL_HU,
  type FolderSortField,
  parseBarcode,
} from '@persped/shared';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useCompanies, useFolders } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';
import { formatTimestamp } from '../../format';

/**
 * Mappakeresés (spec 13. § US-SEARCH-1/2).
 *
 * A beviteli mező EGYSZERRE szolgálja a billentyűzetes keresést és a
 * csatlakoztatott vonalkódolvasót: egy HID-módú olvasó a DOM szintjén
 * billentyűzet, tehát ugyanez az input fogadja. A spec 5.2 §-a pontosan
 * ezt a két bemenetet írja elő (a kamera feltételes).
 *
 * Ha a beírt/beolvasott érték ÉRVÉNYES vonalkód, a `parseBarcode`
 * kliensoldalon felismeri a típusát, és egyenesen az adatlapra megyünk —
 * ugyanaz a függvény, amit a Zebra app használ, tehát a két kliens nem tud
 * eltérni a formátum értelmezésében.
 */
export function SearchPage() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [status, setStatus] = useState('');
  const [company, setCompany] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [year, setYear] = useState('');
  // A cégek listája az ADATBÁZISBÓL (F1) — a `COMPANY_LABEL_HU` megszűnt.
  const companies = useCompanies();
  const [sortBy, setSortBy] = useState<FolderSortField>('barcode');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // A lista MINDIG betöltődik — a D fázis 7.1 pontja teljes mappalistát kér,
  // nem csak találati listát. A keresőmező ettől függetlenül működik.
  const evSzam = year.trim() ? Number(year.trim()) : undefined;
  const folders = useFolders({
    query: submitted || undefined,
    status: status || undefined,
    company: company || undefined,
    documentType: documentType || undefined,
    // Egy KONKRÉT évre szűrünk: a tartomány mindkét vége ugyanaz az év,
    // a szerver pedig ÁTFEDÉST vizsgál — így egy 2015–2017-es mappa is
    // megjelenik a 2016-os szűrőre.
    yearFrom: evSzam,
    yearTo: evSzam,
    sortBy,
    sortDir,
    page: page + 1,
    pageSize,
  });

  function submit() {
    const value = input.trim();
    if (!value) return;

    // Érvényes vonalkód → közvetlenül az adatlapra, nincs értelme
    // találati listát mutatni egyetlen egzakt egyezésre.
    const parsed = parseBarcode(value);
    if (parsed.ok) {
      if (parsed.kind === 'FOLDER') {
        void navigate(`/mappak/${parsed.barcode}`);
        return;
      }
      void navigate(`/lokaciok/${parsed.barcode}`);
      return;
    }

    setSubmitted(value);
    setPage(0);
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          Mappák
        </Typography>
        <Button variant="contained" component={RouterLink} to="/mappak/uj">
          Új mappa
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <TextField
            label="Vonalkód vagy gerincszöveg"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // A vonalkódolvasók Enter-t küldenek a beolvasás után.
              if (e.key === 'Enter') submit();
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            autoFocus
            fullWidth
          />
          <Typography variant="caption" color="text.secondary">
            Írj be egy vonalkódot vagy gerincszöveg-részletet, majd Enter. Csatlakoztatott
            vonalkódolvasó is használható — ez a mező fogadja a beolvasást.
          </Typography>

          {/*
            Gerincmező-szűrők (C3 befejezése). Ezek az oszlopok indexelve
            vannak, de eddig sem API-szűrő, sem felület nem volt hozzájuk —
            a strukturált mezők csak MEGJELENTEK, keresni nem lehetett rájuk.
          */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <TextField
              select
              size="small"
              label="Státusz"
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(0); }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Mind</MenuItem>
              {Object.entries(FOLDER_STATUS_LABEL_HU).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Cég"
              value={company}
              onChange={(e) => { setCompany(e.target.value); setPage(0); }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Mind</MenuItem>
              {(companies.data ?? []).map(({ code: k, name: v }) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Irattípus"
              value={documentType}
              onChange={(e) => { setDocumentType(e.target.value); setPage(0); }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Mind</MenuItem>
              {Object.entries(DOCUMENT_TYPE_LABEL_HU).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Év"
              value={year}
              onChange={(e) => { setYear(e.target.value.replace(/\D/g, '').slice(0, 4)); setPage(0); }}
              sx={{ width: 110 }}
              placeholder="2016"
            />

            <TextField
              select
              size="small"
              label="Rendezés"
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value as FolderSortField); setPage(0); }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="barcode">Vonalkód</MenuItem>
              <MenuItem value="updatedAt">Módosítva</MenuItem>
              <MenuItem value="createdAt">Létrehozva</MenuItem>
              <MenuItem value="spineCompany">Cég</MenuItem>
              <MenuItem value="spineDocumentType">Irattípus</MenuItem>
              <MenuItem value="spineYearFrom">Év</MenuItem>
            </TextField>

            <Button
              size="small"
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            >
              {sortDir === 'asc' ? 'Növekvő' : 'Csökkenő'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {folders.isError && <ProblemAlert error={folders.error} />}

      {folders.isPending && (
        <Stack sx={{ py: 6, alignItems: 'center' }}>
          <CircularProgress />
        </Stack>
      )}

      {folders.data && (
        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vonalkód</TableCell>
                  <TableCell>Gerincszöveg</TableCell>
                  <TableCell>Státusz</TableCell>
                  <TableCell>Hol van</TableCell>
                  <TableCell>Elérhetőség</TableCell>
                  <TableCell>Módosítva</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {folders.data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {submitted ? `Nincs találat: „${submitted}"` : 'Nincs a szűrőnek megfelelő mappa.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {folders.data.items.map((f) => (
                  <TableRow
                    key={f.barcode}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => void navigate(`/mappak/${f.barcode}`)}
                  >
                    <TableCell sx={{ fontFamily: 'monospace' }}>{f.barcode}</TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>
                      {f.currentText ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={f.status === 'APPROVED' ? 'success' : 'default'}
                        label={FOLDER_STATUS_LABEL_HU[f.status]}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {f.currentLocation
                        ? f.currentLocation.palletLevel !== null
                          ? `${f.currentLocation.barcode}/${f.currentLocation.palletLevel}`
                          : f.currentLocation.barcode
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {f.currentLocation?.accessibility
                        ? ACCESSIBILITY_LABEL_HU[f.currentLocation.accessibility]
                        : '—'}
                    </TableCell>
                    <TableCell>{formatTimestamp(f.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={folders.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Sorok oldalanként:"
          />
        </Paper>
      )}
    </Stack>
  );
}
